import { X } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@libs/zapi-ui/shadcn";
import { AppButton, FigmaTypography } from "@libs/zapi-ui/custom";
import { useTranslation } from "react-i18next";

type TProps = {
  isOpen: boolean;
  versionName: string;
  projectName?: string;
  isLoading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

export function DeleteVersionModal({ isOpen, versionName, projectName, isLoading, onCancel, onConfirm }: TProps) {
  const { t } = useTranslation("common");
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isLoading && onCancel()}>
      <DialogContent
        onClick={(e) => e.stopPropagation()}
        data-testid="delete-version-modal"
        showCloseButton={false}
        aria-describedby={undefined}
        className="w-[440px] max-w-[440px] gap-4 rounded-xl border-0 p-5 shadow-200 ring-1 ring-inset ring-innoveraZinc-100 sm:rounded-xl"
      >
        <DialogHeader className="space-y-1">
          <DialogTitle asChild>
            <div className="flex h-8 items-center">
              <FigmaTypography component="span" variant="heading-3" color="zinc900">
                {t("versions.deleteTitle")}
              </FigmaTypography>
            </div>
          </DialogTitle>

          <div className="text-left">
            <FigmaTypography variant="body-2" color="zinc700">
              {projectName
                ? t("versions.deleteQuestion", { version: versionName, project: projectName })
                : t("versions.deleteQuestionNoProject", { version: versionName })}
            </FigmaTypography>
            <FigmaTypography variant="body-2" color="zinc700">
              {t("versions.deleteBody")}
            </FigmaTypography>
          </div>
        </DialogHeader>

        <div className="flex items-center justify-end gap-1">
          <AppButton
            type="button"
            appearance="outlined"
            color="zinc150"
            disabled={isLoading}
            onClick={onCancel}
            data-testid="delete-version-cancel-button"
          >
            {t("cancel")}
          </AppButton>
          <AppButton
            type="button"
            color="red400"
            isLoading={isLoading}
            onClick={onConfirm}
            data-testid="delete-version-confirm-button"
          >
            {t("versions.confirm")}
          </AppButton>
        </div>

        <button
          type="button"
          aria-label={t("versions.close")}
          disabled={isLoading}
          onClick={onCancel}
          data-testid="delete-version-close-button"
          className="absolute right-[13px] top-[13px] flex size-4 items-center justify-center text-innoveraZinc-500 hover:text-innoveraZinc-900"
        >
          <X size={16} />
        </button>
      </DialogContent>
    </Dialog>
  );
}
